import { filter } from '@atlaskit/adf-utils/filter';

import { collectExtensionNodes } from '@confluence/adf-analysis-core/entry-points/collectExtensionNodes.ts';
import { parseContentNodesAdf } from '@confluence/adf-analysis-core/entry-points/parseContentNodesAdf.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { isMigratedMacroImplEnabled } from '@confluence/macro-utils/entry-points/evaluateMacroEnablement.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { blogPostsPreloadHandler } from './handlers/blogPostsPreloadHandler.ts';
import { changeHistoryPreloadHandler } from './handlers/changeHistoryPreloadHandler.ts';
import { contentByLabelPreloadHandler } from './handlers/contentByLabelPreloadHandler.ts';
import { contributorsPreloadHandler } from './handlers/contributorsPreloadHandler.ts';
import { detailsSummaryPreloadHandler } from './handlers/detailsSummaryPreloadHandler.ts';
import { listLabelsPreloadHandler } from './handlers/listLabelsPreloadHandler.ts';
import { pageIncludePreloadHandler } from './handlers/pageIncludePreloadHandler.ts';
import { recentlyUpdatedPreloadHandler } from './handlers/recentlyUpdatedPreloadHandler.ts';
import { pagetreePreloadHandler } from './handlers/pagetreePreloadHandler.ts';
import { pagetreeSearchPreloadHandler } from './handlers/pagetreeSearchPreloadHandler.ts';
import { popularLabelsPreloadHandler } from './handlers/popularLabelsPreloadHandler.ts';
import { profilePicturePreloadHandler } from './handlers/profilePicturePreloadHandler.ts';
import { userProfilePreloadHandler } from './handlers/userProfilePreloadHandler.ts';
import { viewPdfPreloadHandler } from './handlers/viewPdfPreloadHandler.ts';
import { viewXlsPreloadHandler } from './handlers/viewXlsPreloadHandler.ts';
import { tasksReportPreloadHandler } from './handlers/tasksReportPreloadHandler.ts';

/**
 * An ADF extension node representing a macro instance in the document.
 */
export interface MacroContentNode {
	type: string;
	attrs?: {
		extensionKey?: string;
		parameters?: {
			macroParams?: Record<string, { value: string }>;
		};
	};
}

/**
 * Context passed to each macro preload handler, containing information
 * about the content being preloaded.
 */
export interface MacroPreloadContext {
	contentId: string;
	spaceKey: string;
}

/**
 * The set of routes that a macro preload handler can opt into.
 *
 * Each registered handler declares the routes on which it is safe and
 * useful to preload its data via the `routes` field on
 * {@link MacroPreloadHandler}. Callers of {@link preloadMacroData} pass in
 * the current route, and only handlers whose `routes` include that route
 * will be invoked.
 *
 * - `view`            — view-page route (rendering published page content).
 * - `space-overview`  — space overview / homepage route.
 * - `edit`            — full-page editor route.
 * - `live`            — live edit / live doc route.
 */
export type MacroPreloadRoute = 'view' | 'space-overview' | 'edit' | 'live';

/**
 * A handler that knows how to preload data for a specific macro type.
 *
 * Each macro that supports preloading should implement this interface and
 * register its handler with `preloadMacroData`.
 */
export interface MacroPreloadHandler {
	/**
	 * The ADF extension key that identifies this macro type
	 * (e.g. 'contributors', 'detailssummary').
	 */
	extensionKey: string;

	/**
	 * The set of routes on which this handler is allowed to run.
	 *
	 * `preloadMacroData` is invoked from several different route preloaders
	 * (view page, space overview, edit, live). Some macro queries are only
	 * worth preloading on a subset of those routes — for example, a handler
	 * that warms a query only used in read-only rendering should not run on
	 * the edit or live routes. List every route on which this handler should
	 * be eligible; a handler whose route is not in this list will be skipped
	 * for that call.
	 */
	routes: readonly MacroPreloadRoute[];

	/**
	 * Preloads data for a single instance of this macro found in the content.
	 *
	 * @param node - The ADF extension node matching this handler's extensionKey
	 * @param context - Content context (contentId, spaceKey, etc.)
	 * @returns A promise that resolves when preloading is complete
	 */
	preload: (node: MacroContentNode, context: MacroPreloadContext) => Promise<void>;
}

const EXTENSION_TYPES = new Set(['extension', 'bodiedExtension', 'inlineExtension']);

/**
 * Returns a map of extensionKey → handler for all active macro preload
 * handlers based on current experiment gates and the current route.
 *
 * A handler is considered active when:
 *  - its underlying migration experiment is enabled
 *    (`isMigratedMacroImplEnabled`), AND
 *  - its `routes` configuration includes the current `route`.
 */
const getActiveHandlers = (route: MacroPreloadRoute): Map<string, MacroPreloadHandler> => {
	const handlers = new Map<string, MacroPreloadHandler>();

	const register = (handler: MacroPreloadHandler) => {
		if (!handler.routes.includes(route)) {
			return;
		}
		if (!isMigratedMacroImplEnabled(handler.extensionKey)) {
			return;
		}
		handlers.set(handler.extensionKey, handler);
	};

	register(blogPostsPreloadHandler);
	register(changeHistoryPreloadHandler);
	register(contentByLabelPreloadHandler);
	register(contributorsPreloadHandler);
	register(detailsSummaryPreloadHandler);
	register(pageIncludePreloadHandler);
	register(recentlyUpdatedPreloadHandler);
	register(listLabelsPreloadHandler);
	register(popularLabelsPreloadHandler);
	register(pagetreePreloadHandler);
	register(pagetreeSearchPreloadHandler);
	register(profilePicturePreloadHandler);
	register(userProfilePreloadHandler);
	register(viewPdfPreloadHandler);
	register(tasksReportPreloadHandler);
	register(viewXlsPreloadHandler);

	return handlers;
};

/**
 * Resolves content nodes (which may be a promise), validates the body structure,
 * and parses the ADF JSON into a document object.
 *
 * Returns null if the content nodes are invalid, the representation is not
 * 'atlas_doc_format', or the ADF JSON cannot be parsed.
 */
const resolveAndParseADF = async (contentNodes: any): Promise<any | null> => {
	const resolvedNodes =
		contentNodes && typeof contentNodes.then === 'function' ? await contentNodes : contentNodes;

	if (!resolvedNodes?.[0]?.body?.dynamic?.value) {
		return null;
	}

	if (resolvedNodes[0].body.dynamic.representation !== 'atlas_doc_format') {
		return null;
	}

	try {
		return JSON.parse(resolvedNodes[0].body.dynamic.value);
	} catch {
		return null;
	}
};

const resolveContentNodes = async (contentNodes: any): Promise<any> =>
	contentNodes && typeof contentNodes.then === 'function' ? await contentNodes : contentNodes;

/**
 * Shape of the result returned by `preloadCollabDraftContent`. Only the
 * fields that this module needs are typed — additional fields are ignored.
 */
export interface CollabDraftPreloadResult {
	data?: {
		collabDraft?: {
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			document?: unknown;
		} | null;
	} | null;
}

/**
 * The type of the promise passed to {@link preloadMacroDataFromCollabDraft}
 * and {@link resolveCollabDraftToADF}. Mirrors the return type of
 * `preloadCollabDraftContent`, which may resolve to a result, `undefined`,
 * or `null` depending on the preload path.
 */
export type CollabDraftPromise = Promise<CollabDraftPreloadResult | undefined | null>;

// Maximum number of JSON.parse iterations to apply to a stringified
// document. In practice the wire shape is at most double-encoded, so 3
// iterations gives us headroom while still bounding the loop.
const MAX_DOCUMENT_DECODE_PASSES = 3;

/**
 * Decodes a collab-draft document value into a plain object.
 *
 * Handles all three on-the-wire shapes observed in practice for
 * `result.data.collabDraft.document`:
 *
 *   1. An already-parsed object — returned as-is.
 *   2. A single-encoded JSON string — `JSON.parse` once → object.
 *   3. A double-encoded JSON string (the wire form returned by some
 *      Apollo/preload paths) — `JSON.parse` yields another JSON string,
 *      which must be parsed again to recover the object.
 *
 * Returns `null` when `document` is `null`/`undefined`, when any
 * intermediate `JSON.parse` call throws, or when the value never
 * resolves to an object within `MAX_DOCUMENT_DECODE_PASSES` iterations
 * (e.g. a JSON-encoded primitive such as `"42"`).
 */
export const decodeCollabDraftDocument = (document: unknown): object | null => {
	if (document == null) {
		return null;
	}

	let candidate: unknown = document;

	for (let pass = 0; pass < MAX_DOCUMENT_DECODE_PASSES; pass++) {
		if (typeof candidate !== 'string') {
			break;
		}
		try {
			candidate = JSON.parse(candidate);
		} catch {
			return null;
		}
	}

	// Still a string after exhausting all decode passes, or resolved to a
	// non-object primitive (number, boolean, etc.) — give up.
	if (typeof candidate !== 'object') {
		return null;
	}

	return candidate;
};

/**
 * Resolves a `preloadCollabDraftContent` promise, extracts the document
 * field, and decodes it to an ADF object via {@link decodeCollabDraftDocument}.
 *
 * Returns `null` when the promise rejects, when the result has no
 * `data.collabDraft.document`, or when the document cannot be decoded.
 */
const resolveCollabDraftToADF = async (
	collabDraftPromise: CollabDraftPromise,
): Promise<object | null> => {
	let result: CollabDraftPreloadResult | undefined | null;
	try {
		result = await collabDraftPromise;
	} catch {
		return null;
	}

	return decodeCollabDraftDocument(result?.data?.collabDraft?.document);
};

/**
 * Parses the comma-separated blocklist string from Statsig into a list of
 * trimmed, non-empty regex pattern strings.
 */
const getBlocklist = (): string[] => {
	const raw = StatsigConfigurations.getString('confluence-extensions-server-render-blocklist', '');
	const entries = raw.split(',');
	const result: string[] = [];
	for (let i = 0; i < entries.length; i++) {
		const trimmed = entries[i].trim();
		if (trimmed) {
			result.push(trimmed);
		}
	}
	return result;
};

/**
 * Returns true when `extensionKey` is present in the blocklist.
 *
 * The blocklist is sourced from a single Statsig dynamic config
 * (`confluence-extensions-server-render-blocklist`) so it controls both SSR
 * macro query preloading and supplementary macro data preloading.
 */
const isBlocklisted = (blocklist: string[], extensionKey: string): boolean => {
	if (blocklist.length === 0) {
		return false;
	}

	return blocklist.includes(extensionKey);
};

/**
 * Default cap on the number of extensions preloaded per content node when
 * the `cc_ssr_max_extensions_to_preload` Statsig dynamic config is not set.
 */
const DEFAULT_MAX_EXTENSIONS_TO_PRELOAD = 8;

/**
 * Default cap on the number of ADF nodes traversed when scanning the
 * document for preloadable extensions, used when the
 * `cc_ssr_macros_max_nodes_to_iterative_over` Statsig dynamic config is
 * not set.
 */
const DEFAULT_MAX_NODES_TO_ITERATE_OVER = -1;

/**
 * Returns the maximum number of extensions to preload, sourced from the
 * `cc_ssr_max_extensions_to_preload` Statsig dynamic config.
 *
 * A non-positive or non-finite value is treated as "no limit" and is
 * returned as the sentinel `-1` so callers can opt out by configuring `0`
 * (or any negative value).
 */
const getMaxExtensionsToPreload = (): number => {
	const value = StatsigConfigurations.getNumber(
		'cc_ssr_max_extensions_to_preload',
		DEFAULT_MAX_EXTENSIONS_TO_PRELOAD,
	);

	if (!Number.isFinite(value) || value < 0) {
		return -1;
	}
	return Math.floor(value);
};

/**
 * Returns the maximum number of ADF nodes to iterate over when searching
 * for preloadable extension nodes, sourced from the
 * `cc_ssr_macros_max_nodes_to_iterative_over` Statsig dynamic config.
 *
 * A non-positive or non-finite value is treated as "no limit" and is
 * returned as the sentinel `-1` so callers can opt out by configuring `0`
 * (or any negative value).
 */
const getMaxNodesToIterateOver = (): number => {
	const value = StatsigConfigurations.getNumber(
		'cc_ssr_macros_max_nodes_to_iterative_over',
		DEFAULT_MAX_NODES_TO_ITERATE_OVER,
	);

	if (!Number.isFinite(value) || value < 0) {
		return -1;
	}
	return Math.floor(value);
};

/**
 * Filters extension nodes from the parsed ADF document that have a
 * registered preload handler and are not excluded by the SSR extension
 * blocklist.
 *
 * Two Statsig dynamic configs control the work done here:
 * - `cc_ssr_macros_max_nodes_to_iterative_over` caps the number of ADF
 *   nodes visited during traversal (default -1). A value of `-1` means
 *   no limit.
 * - `cc_ssr_max_extensions_to_preload` caps the number of matched
 *   extensions returned (default 8). A value of `-1` means no limit.
 */
const createIsPreloadableMacroNode =
	(handlersByKey: Map<string, MacroPreloadHandler>, blocklist: string[]) =>
	(node: any): boolean => {
		const extensionKey = node.attrs?.extensionKey;
		return (
			EXTENSION_TYPES.has(node.type) &&
			typeof extensionKey === 'string' &&
			handlersByKey.has(extensionKey) &&
			!isBlocklisted(blocklist, extensionKey)
		);
	};

const capPreloadableMacroNodes = (matched: MacroContentNode[]): MacroContentNode[] => {
	const maxExtensionsToPreload = getMaxExtensionsToPreload();
	if (maxExtensionsToPreload === -1 || matched.length <= maxExtensionsToPreload) {
		return matched;
	}
	return matched.slice(0, maxExtensionsToPreload);
};

const getPreloadableMacroNodes = (
	documentADF: any,
	handlersByKey: Map<string, MacroPreloadHandler>,
): MacroContentNode[] => {
	const isPreloadableMacroNode = createIsPreloadableMacroNode(handlersByKey, getBlocklist());
	const maxNodesToIterateOver = getMaxNodesToIterateOver();

	// Track how many ADF nodes the traversal has visited so we can short-
	// circuit once `cc_ssr_macros_max_nodes_to_iterative_over` is reached.
	// `filter` from @atlaskit/adf-utils/traverse walks the full document tree
	// (the document object itself is not an array), unlike `Array#filter`.
	let visited = 0;
	const matched = filter(documentADF, (node: any) => {
		if (maxNodesToIterateOver !== -1 && visited >= maxNodesToIterateOver) {
			return false;
		}
		visited++;
		return isPreloadableMacroNode(node);
	}) as MacroContentNode[];

	return capPreloadableMacroNodes(matched);
};

const getPreloadableMacroNodesFromSharedTraversal = (
	documentADF: any,
	handlersByKey: Map<string, MacroPreloadHandler>,
): MacroContentNode[] => {
	const isPreloadableMacroNode = createIsPreloadableMacroNode(handlersByKey, getBlocklist());
	const matched = collectExtensionNodes(documentADF).filter(
		isPreloadableMacroNode,
	) as MacroContentNode[];

	return capPreloadableMacroNodes(matched);
};

/**
 * Shared execution core: walks the already-resolved ADF document through the
 * active handler pipeline for the given route.
 */
const runPreloadHandlers = async (
	matchedNodes: MacroContentNode[],
	handlersByKey: Map<string, MacroPreloadHandler>,
	context: MacroPreloadContext,
): Promise<void> => {
	if (matchedNodes.length === 0) {
		return;
	}

	// eslint-disable-next-line check-preloaders/no-promise-all
	await Promise.all(
		matchedNodes.map((node) => {
			const handler = handlersByKey.get(node.attrs!.extensionKey!)!;
			return handler.preload(node, context);
		}),
	);
};

/**
 * Preloads data for all registered macro types found in the content.
 *
 * This function centralizes the macro preloading pattern for view/space-overview
 * routes by:
 * 1. Determining which macro handlers are active (based on experiment gates
 *    AND the current route — each handler declares the routes it supports
 *    via its `routes` field)
 * 2. Resolving the source ADF document from the `contentNodes` shape
 *    returned by `preloadContent`.
 * 3. Filtering for extension nodes that have a registered handler.
 * 4. Calling the corresponding handler for each matched node in parallel.
 *
 * For edit/live routes where the ADF comes from a collab draft, use
 * {@link preloadMacroDataFromCollabDraft} instead.
 *
 * @param params.contentNodes - The raw content nodes (or a Promise resolving
 *   to them) in the `[{ body: { dynamic: { value, representation }}}]` shape
 *   returned by `preloadContent`.
 * @param params.context - Content context passed through to each handler.
 * @param params.route - The current route this preload is running for. Only
 *   handlers whose `routes` include this value will be invoked.
 */
export const preloadMacroData = async ({
	contentNodes,
	context,
	route,
}: {
	contentNodes?: any;
	context: MacroPreloadContext;
	route: MacroPreloadRoute;
}): Promise<void> => {
	const handlersByKey = getActiveHandlers(route);
	if (handlersByKey.size === 0) {
		return;
	}

	let matchedNodes: MacroContentNode[];
	if (expValEquals('cc-proj-xl-dedup-macro-preload-adf-parsing', 'isEnabled', true)) {
		const documentADF = parseContentNodesAdf(await resolveContentNodes(contentNodes));
		if (!documentADF) {
			return;
		}
		matchedNodes = getPreloadableMacroNodesFromSharedTraversal(documentADF, handlersByKey);
	} else {
		const documentADF = await resolveAndParseADF(contentNodes);
		if (!documentADF) {
			return;
		}
		matchedNodes = getPreloadableMacroNodes(documentADF, handlersByKey);
	}

	await runPreloadHandlers(matchedNodes, handlersByKey, context);
};

/**
 * Preloads data for all registered macro types found in the content,
 * sourcing the ADF document from a `preloadCollabDraftContent` promise.
 *
 * This function is intended for edit/live routes where the document is not
 * yet available as content nodes but comes from the collab draft preload:
 * 1. Determining which macro handlers are active (based on experiment gates
 *    AND the current route — each handler declares the routes it supports
 *    via its `routes` field)
 * 2. Resolving the ADF document from the `preloadCollabDraftContent` promise.
 *    The document is extracted from `result.data.collabDraft.document`.
 * 3. Filtering for extension nodes that have a registered handler.
 * 4. Calling the corresponding handler for each matched node in parallel.
 *
 * For view/space-overview routes where the ADF comes from content nodes, use
 * {@link preloadMacroData} instead.
 *
 * @param params.collabDraftPromise - A Promise that resolves to the result of
 *   `preloadCollabDraftContent`. The ADF document is extracted from
 *   `result.data.collabDraft.document` and used as the source of truth.
 * @param params.context - Content context passed through to each handler.
 * @param params.route - The current route this preload is running for. Only
 *   handlers whose `routes` include this value will be invoked.
 */
export const preloadMacroDataFromCollabDraft = async ({
	collabDraftPromise,
	context,
	route,
}: {
	collabDraftPromise: CollabDraftPromise;
	context: MacroPreloadContext;
	route: MacroPreloadRoute;
}): Promise<void> => {
	const handlersByKey = getActiveHandlers(route);
	if (handlersByKey.size === 0) {
		return;
	}

	const documentADF = await resolveCollabDraftToADF(collabDraftPromise);
	if (!documentADF) {
		return;
	}

	await runPreloadHandlers(
		getPreloadableMacroNodes(documentADF, handlersByKey),
		handlersByKey,
		context,
	);
};
